/*eslint max-lines-per-function: ["error", 220]*/
import React, { useMemo } from 'react';
import { Accordion } from 'react-bootstrap';

import {
  GetEstimateByMonthDto,
  GetEstimateByMonthDtoStatusEnum,
  GetProjectDto,
} from '../../../common/pokCore/autogenerated/pokApiClient';
import momencik from '../../../common/momencik';
import {
  ProjectEstimatesStatusEnum,
  ProjectEstimatesStatusObj,
} from '../../../common/pokCore/validation/enums';
import { usePokCore } from '../../../common/hooks/usePokCore';
import { useNotifications } from '../../../common/hooks/useNotifications';
import mathUtils from '../../../utils/mathUtils';
import numberFormatter from '../../../common/numberFormatter';
import { useTranslation } from '../../../common/hooks/useTranslation';

import { EstimateProfileTabs } from './EstimateProfileTabs';

interface EstimateProfileAccordionProps {
  estimateByMonth: GetEstimateByMonthDto;
  activeItemIdx?: string;
  project?: GetProjectDto;
  paeTeam: boolean;
  refresh: boolean;
  handleOnClick: () => void;
  onDelete: (id?: string) => Promise<{
    saved: boolean;
    errors: string[];
  }>;
  clone: (
    id: string,
    date: Date,
    withAmounts: boolean,
  ) => Promise<{
    saved: boolean;
    errors: string[];
  }>;
}

export const EstimateProfileAccordion: React.FC<
  EstimateProfileAccordionProps
> = props => {
  const showMargin = !!props.project?.purchaser?.company?.showMargin;
  const { t, tk } = useTranslation('projectLng');
  const [status, setStatus] = React.useState<ProjectEstimatesStatusEnum>(
    props.estimateByMonth?.status as ProjectEstimatesStatusEnum,
  );
  const [refresh, setRefresh] = React.useState<boolean>(false);
  const [saleGlobalBlocked, setSaleGlobalBlocked] =
    React.useState<boolean>(false);
  const [purchaseGlobalBlocked, setPurchaseGlobalBlocked] =
    React.useState<boolean>(false);
  const [estimateSums, setEstimateSums] = React.useState({
    sumSalesNetTotal: 0,
    sumPurchaseNetTotal: 0,
    sumPurchaseInvoicesSum: 0,
  });

  const pok = usePokCore();
  const notifications = useNotifications();

  useMemo(() => {
    pok.estimateItems
      .findByEstimateMonth(props.estimateByMonth.id)
      .then(res => {
        let sumSalesNetTotal = 0;
        let sumPurchaseNetTotal = 0;
        let sumPurchaseInvoicesSum = 0;
        res?.forEach(item => {
          sumSalesNetTotal = mathUtils.add(
            sumSalesNetTotal,
            item.salesNetTotal,
          );
          sumPurchaseNetTotal = mathUtils.add(
            sumPurchaseNetTotal,
            item.purchaseNetTotal,
          );
          sumPurchaseInvoicesSum = mathUtils.add(
            sumPurchaseInvoicesSum,
            item.purchaseInvoicesSum,
          );
        });
        setEstimateSums({
          sumSalesNetTotal: sumSalesNetTotal,
          sumPurchaseNetTotal: sumPurchaseNetTotal,
          sumPurchaseInvoicesSum: sumPurchaseInvoicesSum,
        });
      })
      .catch(err => {
        notifications.caughtError(err);
      });
    pok.estimatesByMonth
      .getById(props.estimateByMonth.id)
      .then(res => {
        setStatus(res.status as ProjectEstimatesStatusEnum);
      })
      .catch(err => {
        notifications.caughtError(err);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    props.estimateByMonth.id,
    pok.estimateItems,
    notifications,
    refresh,
    props.refresh,
  ]);

  useMemo(() => {
    pok.blockades
      .checkBlockedMonth(
        props.estimateByMonth.date,
        pok.getCompanyId(),
        props.project?.projectTeams?.map(o => o.team?.id) || [],
        true,
        false,
        props.project?.id,
      )
      .then(res => {
        setSaleGlobalBlocked(res.toString() === 'true');
      })
      .catch(err => {
        notifications.caughtError(err);
      });
    pok.blockades
      .checkBlockedMonth(
        props.estimateByMonth.date,
        pok.getCompanyId(),
        props.project?.projectTeams?.map(o => o.team?.id) || [],
        false,
        true,
        props.project?.id,
      )
      .then(res => {
        setPurchaseGlobalBlocked(res.toString() === 'true');
      })
      .catch(err => {
        notifications.caughtError(err);
      });
  }, [pok, props.estimateByMonth, props.project, notifications]);

  const generateTitle = () => {
    return (
      <section
        className={`layout-accordion-header ${
          props.estimateByMonth.status ===
          GetEstimateByMonthDtoStatusEnum.Cancelled
            ? 'deleted-item-accordion'
            : ''
        }`}
      >
        <div className="grow1">
          <b>{props.estimateByMonth?.profile.name}</b>&nbsp;
          {momencik(props.estimateByMonth?.date, 'YYYY-MM')}{' '}
          {t(ProjectEstimatesStatusObj[status])}
        </div>
        <div className="marginLeft">
          {`${t(tk.estimates.sales)}: `}
          <b>{numberFormatter(estimateSums.sumSalesNetTotal, true)}</b>{' '}
        </div>
        <div className="marginLeft">
          {`${t(tk.estimates.purchase)}: `}
          <b>{numberFormatter(estimateSums.sumPurchaseNetTotal, true)}</b>{' '}
        </div>
        <div className="marginLeft">
          {`${t(tk.estimates.fvz)}: `}
          <b>
            {numberFormatter(estimateSums.sumPurchaseInvoicesSum, true)}
          </b>{' '}
        </div>
        <div className="marginLeft">
          {`${t(tk.estimates.fvzDiff)}: `}
          <b>
            {numberFormatter(
              mathUtils.subtract(
                estimateSums.sumPurchaseNetTotal || 0,
                estimateSums.sumPurchaseInvoicesSum || 0,
              ),
              true,
            )}
          </b>{' '}
        </div>
        {showMargin && (
          <div className="marginLeft">
            {`${t(tk.estimates.margin)}: `}
            <b>
              {numberFormatter(
                mathUtils.subtract(
                  estimateSums.sumSalesNetTotal || 0,
                  estimateSums.sumPurchaseNetTotal || 0,
                ),
                true,
              )}
            </b>{' '}
          </div>
        )}
      </section>
    );
  };

  const onRefresh = () => {
    setRefresh(!refresh);
  };

  return (
    <Accordion.Item
      eventKey={props.estimateByMonth.id}
      className={`flex-fill estimate-profile-accordion ${
        props.activeItemIdx === props.estimateByMonth.id
          ? 'active-item-accordion'
          : ''
      }`}
    >
      <Accordion.Header
        onClick={props.handleOnClick}
        className={`${
          props.activeItemIdx === props.estimateByMonth.id
            ? ''
            : 'profile-accordion'
        }`}
      >
        {generateTitle()}
      </Accordion.Header>
      <Accordion.Body>
        {props.activeItemIdx === props.estimateByMonth.id && (
          <EstimateProfileTabs
            estimateByMonth={props.estimateByMonth}
            project={props.project}
            paeTeam={props.paeTeam}
            onDelete={props.onDelete}
            updateStatus={setStatus}
            onRefresh={onRefresh}
            clone={props.clone}
            saleGlobalBlocked={saleGlobalBlocked}
            purchaseGlobalBlocked={purchaseGlobalBlocked}
          />
        )}
      </Accordion.Body>
    </Accordion.Item>
  );
};
